import React, { useState, useEffect } from 'react';
import { 
  Layout, TrendingUp, AlertTriangle, Package,
  Camera, FileText, TestTube, BarChart3,
  Eye, Settings, Download, RefreshCw
} from 'lucide-react';
import { colors } from '../../../constants/theme';
import { Card } from '../../ui/Card';
import { Button } from '../../ui/Button';
import { Badge } from '../../ui/Badge';
import EfficiencyAnalysis from './EfficiencyAnalysis';
import PlanogramGenerator from './PlanogramGenerator';
import RuleExtractor from './RuleExtractor';
import ShelfImageAnalyzer from './ShelfImageAnalyzer';
import ABTestPlanner from './ABTestPlanner';
import InstructionGenerator from './InstructionGenerator';
import EffectTracker from './EffectTracker';
import VisualPlanogram from './VisualPlanogram';
import BeverageShelfVisualizer from './BeverageShelfVisualizer';
import SeasonalCrossMDVisualizer from './SeasonalCrossMDVisualizer';
import AgentCommunicationBus from '../../../services/AgentCommunicationBus';

interface ShelfItem {
  skuId: string;
  skuName: string;
  category: string;
  position: { shelf: number; row: number; column: number };
  facings: number;
  salesPerDay: number;
  profitMargin: number;
  turnoverRate: number;
  status: 'optimal' | 'underperforming' | 'overperforming' | 'dead';
}

interface PlanogramPattern {
  id: string;
  name: string;
  description: string;
  expectedSalesIncrease: number;
  expectedProfitIncrease: number;
  changes: number;
  confidence: number;
  rules: string[];
}

const PlanogramAgent: React.FC = () => {
  const [activeTab, setActiveTab] = useState('visual');
  const [selectedStore, setSelectedStore] = useState('shibuya');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [shelfItems, setShelfItems] = useState<ShelfItem[]>([]);
  const [planogramPatterns, setPlanogramPatterns] = useState<PlanogramPattern[]>([]);
  const [isOptimizing, setIsOptimizing] = useState(false);
  const [stockData, setStockData] = useState<any>(null);
  const [demandData, setDemandData] = useState<any>(null);
  
  // KPI
  const [shelfEfficiency, setShelfEfficiency] = useState(72.5);
  const [deadStock, setDeadStock] = useState(8);
  const [profitPerShelf, setProfitPerShelf] = useState(125000);
  const [complianceRate, setComplianceRate] = useState(85);

  // エージェント登録とメッセージ受信
  useEffect(() => {
    AgentCommunicationBus.register(
      'planogram',
      ['stock_levels', 'demand_insights', 'campaign_info'],
      (message) => {
        console.log('[Planogram] Received message:', message);
        
        // 在庫データ受信
        if (message.topic === 'stock_levels' && message.from === 'inventory') {
          setStockData(message.payload);
          updateShelfOptimization(message.payload.stockItems);
        }
        
        // 需要予測データ受信
        if (message.topic === 'demand_insights' && message.from === 'demand_forecast') {
          setDemandData(message.payload);
        }
        
        // キャンペーン情報受信
        if (message.topic === 'campaign_info' && message.from === '52week_md') {
          updatePromotionAlignment(message.payload);
        }
      }
    );

    // 初期データ生成
    generateMockData();

    return () => {
      AgentCommunicationBus.unregister('planogram');
    };
  }, []);

  // モックデータ生成
  const generateMockData = () => {
    const categories = ['飲料', '菓子', '日配品', '冷凍食品', '生鮮'];
    const items: ShelfItem[] = [];
    
    for (let shelf = 1; shelf <= 5; shelf++) {
      for (let row = 1; row <= 4; row++) {
        for (let col = 1; col <= 8; col++) {
          const category = categories[shelf - 1];
          const sales = Math.random() * 1000 + 100;
          const margin = 15 + Math.random() * 20;
          const turnover = Math.random() * 20 + 5;
          
          let status: 'optimal' | 'underperforming' | 'overperforming' | 'dead';
          if (turnover < 8) status = 'dead';
          else if (sales < 300) status = 'underperforming';
          else if (sales > 800) status = 'overperforming';
          else status = 'optimal';
          
          items.push({
            skuId: `SKU${String(shelf).padStart(2, '0')}${String(row).padStart(2, '0')}${String(col).padStart(2, '0')}`,
            skuName: `商品${shelf}-${row}-${col}`,
            category,
            position: { shelf, row, column: col },
            facings: Math.floor(Math.random() * 3) + 1,
            salesPerDay: sales,
            profitMargin: margin,
            turnoverRate: turnover,
            status
          });
        }
      }
    }
    
    setShelfItems(items);
    generatePlanogramPatterns();
  };

  // Planogramパターン生成
  const generatePlanogramPatterns = () => {
    const patterns: PlanogramPattern[] = [
      {
        id: 'PLN001',
        name: '売上最大化パターン',
        description: '売上上位商品をゴールデンゾーンに配置し、フェーシングを拡大',
        expectedSalesIncrease: 15,
        expectedProfitIncrease: 12,
        changes: 45,
        confidence: 85,
        rules: [
          '売上TOP20をアイレベルに配置',
          '回転率10回/月以上の商品のフェース拡大',
          'クロスMD商品を隣接配置'
        ]
      },
      {
        id: 'PLN002',
        name: '利益重視パターン',
        description: '粗利率の高い商品を優先的に目立つ位置に配置',
        expectedSalesIncrease: 8,
        expectedProfitIncrease: 18,
        changes: 38,
        confidence: 78,
        rules: [
          '粗利率30%以上を優先配置',
          'PB商品のフェース2倍',
          '低利益商品を下段に移動'
        ]
      },
      {
        id: 'PLN003',
        name: '季節最適化パターン',
        description: '季節商品と関連商品をセット陳列で訴求力向上',
        expectedSalesIncrease: 20,
        expectedProfitIncrease: 15,
        changes: 52,
        confidence: 72,
        rules: [
          '季節商品を入口付近に配置',
          '関連購買商品を隣接配置',
          'イベント商品の大量陳列'
        ]
      },
      {
        id: 'PLN004',
        name: '在庫効率化パターン',
        description: '在庫回転率を重視し、死筋商品を削減',
        expectedSalesIncrease: 5,
        expectedProfitIncrease: 10,
        changes: 30,
        confidence: 90,
        rules: [
          '回転率5回/月未満を削減',
          '過剰在庫商品の特売配置',
          'フェーシング数を在庫に連動'
        ]
      }
    ];
    
    setPlanogramPatterns(patterns);
  };

  // 在庫データに基づく最適化更新
  const updateShelfOptimization = (stockItems: any[]) => {
    // 在庫レベルに応じて棚割りを調整
    const updatedItems = shelfItems.map(item => {
      const stock = stockItems.find(s => s.skuId === item.skuId);
      if (stock) {
        // 在庫過多の商品は前出し推奨
        if (stock.status === 'excess') {
          return { ...item, facings: Math.min(4, item.facings + 1) };
        }
        // 在庫不足の商品はフェース縮小
        if (stock.status === 'critical' || stock.status === 'low') {
          return { ...item, facings: Math.max(1, item.facings - 1) };
        }
      }
      return item;
    });
    
    setShelfItems(updatedItems);
  };

  // 販促との整合性チェック
  const updatePromotionAlignment = (campaignInfo: any) => {
    // キャンペーン商品が目立つ位置にあるか確認
    console.log('Checking promotion alignment:', campaignInfo);
  };

  // 最適化実行
  const runOptimization = () => {
    setIsOptimizing(true);
    
    // チラシエージェントに棚配置情報を送信
    AgentCommunicationBus.send({
      from: 'planogram',
      to: 'flyer',
      type: 'update',
      topic: 'shelf_layout',
      payload: {
        featuredItems: shelfItems.filter(i => i.status === 'overperforming'),
        promotionZones: planogramPatterns[0].rules
      },
      priority: 'medium'
    });
    
    // MDエージェントに棚効率を送信
    AgentCommunicationBus.send({
      from: 'planogram',
      to: '52week_md',
      type: 'notification',
      topic: 'shelf_efficiency',
      payload: {
        efficiency: shelfEfficiency,
        deadStockCount: deadStock,
        recommendations: planogramPatterns.map(p => ({
          pattern: p.name,
          expectedIncrease: p.expectedSalesIncrease
        }))
      },
      priority: 'low'
    });
    
    setTimeout(() => {
      setIsOptimizing(false);
      // KPI更新
      const newEfficiency = Math.min(85, shelfEfficiency + Math.random() * 5);
      const newDeadStock = Math.max(3, deadStock - Math.floor(Math.random() * 3));
      const newProfit = profitPerShelf * (1 + Math.random() * 0.1);
      const newCompliance = Math.min(95, complianceRate + Math.random() * 5);
      
      setShelfEfficiency(newEfficiency);
      setDeadStock(newDeadStock);
      setProfitPerShelf(newProfit);
      setComplianceRate(newCompliance);
      
      // 棚割り最適化アクション
      console.log('🏪 棚割りを最適化しました！', {
        efficiency: newEfficiency,
        deadStock: newDeadStock,
        profit: newProfit,
        compliance: newCompliance
      });
      alert(`✅ 棚割りを最適化しました！\n\n最適化結果:\n- 棚効率: ${newEfficiency.toFixed(1)}%\n- デッドストック削減: ${(deadStock - newDeadStock)}品目\n- 棚あたり利益: ${((newProfit/profitPerShelf - 1) * 100).toFixed(1)}%向上\n- 規則遵守率: ${newCompliance.toFixed(1)}%`);
    }, 2000);
  };

  const tabs = [
    { id: 'visual', name: 'ビジュアル棚割り', icon: Eye },
    { id: 'beverage', name: '飲料棚割り', icon: Package },
    { id: 'analysis', name: '効率分析', icon: BarChart3 },
    { id: 'generator', name: 'Planogram生成', icon: Layout },
    { id: 'rules', name: '陳列ルール', icon: Settings },
    { id: 'image', name: '棚画像解析', icon: Camera },
    { id: 'abtest', name: 'ABテスト', icon: TestTube },
    { id: 'instruction', name: '指示書作成', icon: FileText },
    { id: 'tracking', name: '効果測定', icon: TrendingUp },
  ];

  const stores = [
    { id: 'shibuya', name: '渋谷店' },
    { id: 'shinjuku', name: '新宿店' },
    { id: 'ginza', name: '銀座店' },
  ];

  return (
    <div className="h-full overflow-y-auto">
      <div className="p-3">
        {/* コンパクトヘッダー */}
        <div className="mb-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <h2 className="text-lg font-bold" style={{ color: colors.text.primary }}>
                棚割り最適化エージェント
              </h2>
              <span className="text-sm" style={{ color: colors.text.secondary }}>
                売上効率分析から最適なPlanogramを自動生成
              </span>
            </div>
            <div className="flex gap-2">
              <select 
                className="px-2 py-1 text-xs border rounded"
                style={{ borderColor: colors.ui.border }}
                value={selectedStore}
                onChange={(e) => setSelectedStore(e.target.value)}
                aria-label="店舗選択"
              >
                {stores.map(store => (
                  <option key={store.id} value={store.id}>{store.name}</option>
                ))}
              </select>
              <Button variant="primary" size="small" onClick={runOptimization} disabled={isOptimizing}>
                <RefreshCw size={12} className={isOptimizing ? 'animate-spin' : ''} />
                {isOptimizing ? '最適化中' : '最適化'}
              </Button>
            </div>
          </div>
        </div>

        {/* コンパクトKPIカード */}
        <div className="grid grid-cols-4 gap-2 mb-3">
          <KPICard
            title="棚効率"
            value={`${shelfEfficiency.toFixed(1)}%`}
            change="+3.2%"
            icon={<Layout />}
            trend="up"
            color={colors.accent.purple}
          />
          <KPICard
            title="死筋商品"
            value={`${deadStock}品`}
            change="-2品"
            icon={<AlertTriangle />}
            trend="down"
            color={colors.status.warning}
          />
          <KPICard
            title="棚あたり利益"
            value={`¥${(profitPerShelf / 1000).toFixed(0)}K`}
            change="+8.5%"
            icon={<TrendingUp />}
            trend="up"
            color={colors.support.success}
          />
          <KPICard
            title="陳列遵守率"
            value={`${complianceRate.toFixed(0)}%`}
            change="+5.0%"
            icon={<Eye />}
            trend="up"
            color={colors.accent.blue}
          />
        </div>

        {/* タブナビゲーション */}
        <div className="flex gap-1 mb-3 border-b overflow-x-auto" style={{ borderColor: colors.ui.border }}>
          {tabs.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1 px-2 py-1 text-xs font-medium border-b-2 transition-all whitespace-nowrap ${
                  activeTab === tab.id ? 'border-current' : 'border-transparent'
                }`}
                style={{ 
                  color: activeTab === tab.id ? colors.accent.purple : colors.text.secondary,
                  borderColor: activeTab === tab.id ? colors.accent.purple : 'transparent'
                }}
              >
                <Icon size={12} />
                {tab.name}
              </button>
            );
          })}
        </div>

        {/* タブコンテンツ */}
        <div className="space-y-6">
          {activeTab === 'visual' && (
            <SeasonalCrossMDVisualizer 
              viewMode="seasonal"
              onProductClick={(product) => {
                console.log('選択された商品:', product);
              }}
            />
          )}
          
          {activeTab === 'beverage' && (
            <BeverageShelfVisualizer 
              editMode={false}
              onProductClick={(product) => {
                console.log('選択された商品:', product);
              }}
            />
          )}
          
          {activeTab === 'analysis' && (
            <EfficiencyAnalysis 
              shelfItems={shelfItems} 
              selectedCategory={selectedCategory}
              setSelectedCategory={setSelectedCategory}
            />
          )}
          
          {activeTab === 'generator' && (
            <PlanogramGenerator 
              patterns={planogramPatterns}
              shelfItems={shelfItems}
            />
          )}
          
          {activeTab === 'rules' && (
            <RuleExtractor shelfItems={shelfItems} />
          )}
          
          {activeTab === 'image' && (
            <ShelfImageAnalyzer store={selectedStore} />
          )}
          
          {activeTab === 'abtest' && (
            <ABTestPlanner patterns={planogramPatterns} stores={stores} />
          )}
          
          {activeTab === 'instruction' && (
            <InstructionGenerator 
              pattern={planogramPatterns[0]}
              store={selectedStore}
            />
          )}
          
          {activeTab === 'tracking' && (
            <EffectTracker 
              patterns={planogramPatterns}
              shelfEfficiency={shelfEfficiency}
            />
          )}
        </div>
      </div>
    </div>
  );
};

// KPIカードコンポーネント
interface KPICardProps {
  title: string;
  value: string;
  change: string;
  icon: React.ReactNode;
  trend: 'up' | 'down' | 'neutral';
  color: string;
}

const KPICard: React.FC<KPICardProps> = ({ title, value, change, icon, trend, color }) => {
  const trendColor = trend === 'up' ? colors.support.success : 
                     trend === 'down' ? colors.status.error : 
                     colors.text.secondary;
  
  return (
    <Card className="p-2">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <div className="p-1 rounded" style={{ backgroundColor: color + '20' }}>
            {React.isValidElement(icon) ? React.cloneElement(icon, { size: 12, style: { color } }) : icon}
          </div>
          <div className="text-xs" style={{ color: colors.text.secondary }}>
            {title}
          </div>
        </div>
        <span className="text-xs font-medium" style={{ color: trendColor }}>
          {change}
        </span>
      </div>
      <div className="text-lg font-bold" style={{ color: colors.text.primary }}>
        {value}
      </div>
    </Card>
  );
};

export default PlanogramAgent;